import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtMoney } from "@/components/Shared";
import PageShell from "@/components/PageShell";
import SquareReconciliation from "@/components/SquareReconciliation";
import { useI18n } from "@/i18n";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";

const STATUS_COLORS = { paid: "#16a34a", pending: "#d97706", overdue: "#dc2626", canceled: "#94a3b8" };

export default function Reports() {
  const { t, lang } = useI18n();
  const STATUS_LABEL = lang === "en"
    ? { paid: "Paid", pending: "Pending", overdue: "Overdue", canceled: "Canceled" }
    : { paid: "Pagadas", pending: "Pendientes", overdue: "Vencidas", canceled: "Canceladas" };
  const [trend, setTrend] = useState([]);
  const [statusData, setStatusData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [tr, s] = await Promise.all([
          api.get("/reports/revenue-trend?months=6"),
          api.get("/reports/invoice-status"),
        ]);
        setTrend(tr.data);
        setStatusData(s.data.map((d) => ({ ...d, label: STATUS_LABEL[d.status] || d.status })));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [lang]);

  return (
    <PageShell
      section={t("nav.group.finance")}
      page={t("rep.title")}
      context={lang === "en" ? "Last 6 months" : "Últimos 6 meses"}
      testid="reports-toolbar"
    >

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-8">
        <div className="bg-white border border-slate-200 rounded-md p-6 shadow-sm" data-testid="bar-revenue-expenses">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1">Comparativo</div>
          <h3 className="text-lg font-medium text-slate-900 mb-4">Ingresos vs. Gastos por mes</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height={288}>
              <BarChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="label" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="income" name="Ingresos" fill="#16a34a" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expenses" name="Gastos" fill="#ea580c" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-6 shadow-sm" data-testid="pie-invoice-status">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1">Distribución</div>
          <h3 className="text-lg font-medium text-slate-900 mb-4">Estado de facturas</h3>
          <div className="h-72">
            {statusData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-slate-400">Sin datos todavía</div>
            ) : (
              <ResponsiveContainer width="100%" height={288}>
                <PieChart>
                  <Pie data={statusData} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={90} innerRadius={50}>
                    {statusData.map((entry, idx) => (
                      <Cell key={idx} fill={STATUS_COLORS[entry.status] || "#94a3b8"} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6 shadow-sm" data-testid="line-trend">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1">Tendencia</div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Evolución de ingresos</h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height={288}>
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="label" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} />
              <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }} />
              <Line type="monotone" dataKey="income" name="Ingresos" stroke="#0f172a" strokeWidth={2.5} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-6">
        <SquareReconciliation lang={lang} />
      </div>
    </PageShell>
  );
}
